import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Table } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminRateMatrixPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({
        category: '',
        superior: '',
        very_good: '',
        good: '',
        medium: '',
        display_order: 0,
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/rate-matrix');
            if (response.data.success) {
                setRows(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching rate matrix data:', error);
            toast.error('Failed to load rate matrix data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleEdit = (row) => {
        setEditingRow(row);
        setFormData({
            category: row.category,
            superior: row.superior,
            very_good: row.very_good,
            good: row.good,
            medium: row.medium,
            display_order: row.display_order,
            is_active: row.is_active === 1 || row.is_active === true
        });
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingRow(null);
        setFormData({
            category: '',
            superior: '',
            very_good: '',
            good: '',
            medium: '',
            display_order: rows.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingRow) {
                await axios.put(`/api/admin/rate-matrix/${editingRow.id}`, formData);
                toast.success('Rate updated successfully');
            } else {
                await axios.post('/api/admin/rate-matrix', formData);
                toast.success('Rate added successfully');
            }
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving rate:', error);
            toast.error('Failed to save rate');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/rate-matrix/${deleteConfirm.id}`);
            toast.success('Rate deleted successfully');
            setDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting rate:', error);
            toast.error('Failed to delete rate');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Rate Matrix Data</h1>
                    <p className="text-slate-500 mt-1">Manage the interest rate matrix for the FAQ page</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg text-sm"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Category
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12">Loading...</div>
            ) : rows.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <Table className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-slate-800 mb-2">No data available</h3>
                    <p className="text-slate-500">Add categories to populate the rate matrix.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                <tr>
                                    <th className="p-4">Category</th>
                                    <th className="p-4 text-center">Superior</th>
                                    <th className="p-4 text-center">Very Good</th>
                                    <th className="p-4 text-center">Good</th>
                                    <th className="p-4 text-center">Medium</th>
                                    <th className="p-4 text-center">Status</th>
                                    <th className="p-4 text-center">Order</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="p-4 font-medium text-slate-800 break-words max-w-[200px]">{row.category}</td>
                                        <td className="p-4 text-center">{row.superior}</td>
                                        <td className="p-4 text-center">{row.very_good}</td>
                                        <td className="p-4 text-center">{row.good}</td>
                                        <td className="p-4 text-center">{row.medium}</td>
                                        <td className="p-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                {row.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-center text-slate-500">{row.display_order}</td>
                                        <td className="p-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => handleEdit(row)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDelete(row.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="px-8 py-6 border-b border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-800">{editingRow ? 'Edit Category' : 'Add New Category'}</h2>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto font-sans">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Category Description</label>
                                    <textarea
                                        required
                                        rows="2"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. Purchase New/Existing, LAP > 10L, Self Construction"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Superior Rate</label>
                                    <input
                                        type="text"
                                        value={formData.superior}
                                        onChange={(e) => setFormData({ ...formData, superior: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. 15.00%"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Very Good Rate</label>
                                    <input
                                        type="text"
                                        value={formData.very_good}
                                        onChange={(e) => setFormData({ ...formData, very_good: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. 17.00%"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Good Rate</label>
                                    <input
                                        type="text"
                                        value={formData.good}
                                        onChange={(e) => setFormData({ ...formData, good: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. 18.50%"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Medium Rate</label>
                                    <input
                                        type="text"
                                        value={formData.medium}
                                        onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                        placeholder="e.g. 19.50%"
                                    />
                                </div>

                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                                    />
                                    <label htmlFor="isActive" className="text-sm font-semibold text-slate-700">Active</label>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                    <input
                                        type="number"
                                        value={formData.display_order}
                                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-3 rounded-xl font-semibold text-slate-600 hover:bg-slate-100 transition-all font-sans"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 font-sans shadow-lg shadow-primary-200"
                                >
                                    {submitting ? 'Saving...' : 'Save Category'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category? This action cannot be undone."
            />
        </div>
    );
};

export default AdminRateMatrixPage;
